// blockIdPlugin.js
// Lightweight plugin to ensure block nodes created programmatically get IDs
// Most IDs are generated during parsing (see schema.js), this handles edge cases
//
// Generalized "unique per-instance id" mechanism (see ticket 04, map-node-dtg-view-audit):
// in addition to the universal `blockId` attr (injected into every block node by
// addBlockIdToNodes in schema.js), any node spec can declare `uniqueIdAttrs: [attrName, ...]`
// to have those attrs regenerated on paste/duplicate too, exactly like blockId. A node spec can
// also declare `onRegenerateId(oldId, newId, node)` to run a side effect whenever one of those
// declared attrs is regenerated — e.g. to initialize per-instance state a fresh id needs that
// blockId's own regeneration doesn't already cover. (mapNode.js's `nodeId` is a uniqueIdAttrs
// user but, since shared-map-data ticket 01, no longer needs an `onRegenerateId` hook: every
// embed reads/writes the report's one shared map fragment, so a pasted/duplicated node has
// nothing per-instance left to initialize.)
//
// Design note on WHERE the id swap vs. the side-effect hook run:
// - `transformPasted` (below) regenerates attrs only. It runs synchronously as a pure slice
//   transform, before ProseMirror even knows whether the resulting paste will actually land in
//   the document (another plugin's handlePaste could still intercept it, the transaction could
//   be rejected downstream, etc). It must stay side-effect-free.
// - `onRegenerateId` side effects are instead fired from `appendTransaction`, once we know a
//   docChanged transaction has actually been applied to the editor state. This guarantees any
//   such side effect never runs for a regenerated id that never actually made it into the
//   document.
//   `useYDocStore.getState().ydoc` is reachable synchronously from anywhere (it's a plain
//   Zustand store getter, not React-context-bound — see insertMapNode.js/snippetDropPlugin.js
//   for existing precedent), so this split isn't strictly forced by *availability* of the ydoc;
//   it's a deliberate choice to keep transformPasted pure and only run side effects once the
//   change is confirmed to be part of a real transaction.

import {Plugin} from "prosemirror-state";
import {Fragment, Slice} from "prosemirror-model";
import {generateUUID} from "../helpers/utils";

/**
 * Regenerate the universal `blockId` attr (if declared on this node's type) and any
 * declared `spec.uniqueIdAttrs` attrs that currently have a value, on a single node.
 * Queues a `{typeName, attrName, oldId, newId}` entry per regenerated uniqueIdAttrs value into
 * `pendingRegenerations` so `appendTransaction` can later fire `spec.onRegenerateId` once the
 * paste is confirmed to have landed in the document.
 *
 * @param {import('prosemirror-model').Node} node
 * @param {Array<{typeName: string, attrName: string, oldId: string, newId: string}>} pendingRegenerations
 * @returns {{node: import('prosemirror-model').Node, changed: boolean}}
 */
function regenerateNodeIds(node, pendingRegenerations) {
    let nextNode = node;
    let changed = false;

    // Universal blockId handling (unchanged pre-existing behavior).
    if (nextNode.isBlock && nextNode.type.spec?.attrs?.blockId) {
        nextNode = nextNode.type.create(
            {
                ...nextNode.attrs,
                blockId: generateUUID(),
            },
            nextNode.content,
            nextNode.marks
        );
        changed = true;
    }

    // Generalized per-node-type unique id attrs (e.g. mapNode's `nodeId`), additive to blockId.
    const uniqueIdAttrs = nextNode.type.spec?.uniqueIdAttrs;
    if (Array.isArray(uniqueIdAttrs) && uniqueIdAttrs.length > 0) {
        const nextAttrs = {...nextNode.attrs};
        let attrsChanged = false;

        uniqueIdAttrs.forEach((attrName) => {
            // Defensive: only regenerate attrs the node type actually declares.
            if (!nextNode.type.spec?.attrs?.[attrName]) return;

            const oldId = nextNode.attrs?.[attrName];
            if (!oldId) return; // nothing to regenerate; appendTransaction's backfill handles this.

            const newId = generateUUID();
            nextAttrs[attrName] = newId;
            attrsChanged = true;
            pendingRegenerations.push({
                typeName: nextNode.type.name,
                attrName,
                oldId,
                newId,
            });
        });

        if (attrsChanged) {
            nextNode = nextNode.type.create(nextAttrs, nextNode.content, nextNode.marks);
            changed = true;
        }
    }

    return {node: nextNode, changed};
}

function remapPastedFragment(fragment, pendingRegenerations) {
    let changed = false;
    const children = [];

    fragment.forEach((node) => {
        let nextNode = node;

        if (node.childCount > 0) {
            const remappedChild = remapPastedFragment(node.content, pendingRegenerations);
            if (remappedChild.changed) {
                nextNode = node.copy(remappedChild.fragment);
                changed = true;
            }
        }

        if (nextNode.isBlock) {
            const regenerated = regenerateNodeIds(nextNode, pendingRegenerations);
            if (regenerated.changed) {
                nextNode = regenerated.node;
                changed = true;
            }
        }

        children.push(nextNode);
    });

    return {
        changed,
        fragment: changed ? Fragment.fromArray(children) : fragment,
    };
}

/**
 * Check if transaction is from Yjs (collaborative editing)
 * We don't want to interfere with remote changes
 */
function isYjsTransaction(tr) {
    return (
        tr.getMeta("y-sync") != null ||
        tr.getMeta("yjs") != null ||
        tr.getMeta("y-undo") != null ||
        tr.getMeta("y-reset") != null ||
        tr.getMeta("y-sync$") != null
    );
}

/**
 * Create a lightweight plugin that ensures block nodes have IDs
 * Only processes new/changed nodes, not the entire document
 * @returns {Plugin} ProseMirror plugin
 */
export function blockIdPlugin() {
    // Per-plugin-instance queue bridging transformPasted (attrs-only regeneration) to
    // appendTransaction (where onRegenerateId side effects are actually fired). Populated by
    // transformPasted, drained by the very next docChanged appendTransaction pass.
    const pendingRegenerations = [];

    return new Plugin({
        props: {
            transformPasted(slice) {
                const remapped = remapPastedFragment(slice.content, pendingRegenerations);
                if (!remapped.changed) return slice;
                return new Slice(remapped.fragment, slice.openStart, slice.openEnd);
            },
        },
        appendTransaction(transactions, _oldState, newState) {
            // Skip if no document changes
            const docChanged = transactions.some((t) => t.docChanged);
            if (!docChanged) return null;

            const tr = newState.tr;
            let changed = false;
            const hooksToFire = [];

            // Only check nodes in affected ranges from this transaction
            transactions.forEach(transaction => {
                if (isYjsTransaction(transaction)) return;

                transaction.steps.forEach((step, stepIndex) => {
                    const map = transaction.mapping.maps[stepIndex];

                    // Check only the modified range
                    map.forEach((oldStart, oldEnd, newStart, newEnd) => {
                        tr.doc.nodesBetween(newStart, newEnd, (node, pos) => {
                            // Skip non-block nodes
                            if (!node.isBlock) return;

                            // Universal blockId backfill (unchanged pre-existing behavior).
                            if (node.type.spec?.attrs?.blockId && !node.attrs?.blockId) {
                                changed = true;
                                tr.setNodeMarkup(pos, undefined, {
                                    ...node.attrs,
                                    blockId: generateUUID(),
                                });
                            }

                            // Generalized per-node-type unique id attrs: backfill missing values,
                            // and fire onRegenerateId for values regenerated during paste.
                            const uniqueIdAttrs = node.type.spec?.uniqueIdAttrs;
                            if (!Array.isArray(uniqueIdAttrs) || uniqueIdAttrs.length === 0) return;

                            uniqueIdAttrs.forEach((attrName) => {
                                if (!node.type.spec?.attrs?.[attrName]) return;

                                const value = node.attrs?.[attrName];

                                if (!value) {
                                    // Backfill a missing id (mirrors blockId backfill above), and
                                    // treat it the same as a regeneration for onRegenerateId's sake
                                    // so e.g. a programmatically-created mapNode without a nodeId
                                    // still gets its Yjs fragment initialized.
                                    const newId = generateUUID();
                                    changed = true;
                                    tr.setNodeMarkup(pos, undefined, {
                                        ...node.attrs,
                                        [attrName]: newId,
                                    });
                                    hooksToFire.push({node, oldId: null, newId});
                                    return;
                                }

                                const pendingIdx = pendingRegenerations.findIndex(
                                    (p) => p.typeName === node.type.name
                                        && p.attrName === attrName
                                        && p.newId === value
                                );
                                if (pendingIdx !== -1) {
                                    const [pending] = pendingRegenerations.splice(pendingIdx, 1);
                                    hooksToFire.push({node, oldId: pending.oldId, newId: pending.newId});
                                }
                            });
                        });
                    });
                });
            });

            // Fire side-effect hooks after the scan (not mid-traversal) so they never run inside
            // a nodesBetween callback. Runs even if `changed` stays false (a paste that only
            // regenerated already-present ids doesn't need a follow-up setNodeMarkup transaction).
            hooksToFire.forEach(({node, oldId, newId}) => {
                node.type.spec?.onRegenerateId?.(oldId, newId, node);
            });

            // A multi-paragraph paste or an Enter-split copies blockId onto the new sibling.
            // Each paragraph has to be its own block or the version diff collapses them and
            // ICD-203 / Best practices never see the new paragraphs (MINSKY-17884).
            const sawLocalEdit = transactions.some((transaction) => (
                transaction.docChanged && !isYjsTransaction(transaction)
            ));
            if (sawLocalEdit) {
                const seenBlockIds = new Set();
                const duplicatePositions = [];
                tr.doc.descendants((node, pos) => {
                    if (!node.isBlock) return;
                    const blockId = node.attrs?.blockId;
                    if (!blockId) return;
                    if (seenBlockIds.has(blockId)) duplicatePositions.push(pos);
                    else seenBlockIds.add(blockId);
                });
                duplicatePositions.forEach((pos) => {
                    const node = tr.doc.nodeAt(pos);
                    if (!node?.isBlock) return;
                    changed = true;
                    tr.setNodeMarkup(pos, undefined, {
                        ...node.attrs,
                        blockId: generateUUID(),
                    });
                });
            }

            // Any queued regenerations that weren't matched to a landed node this pass are stale
            // (e.g. the paste never made it into the doc) — drop them rather than let them leak
            // across into future, unrelated transactions.
            pendingRegenerations.length = 0;

            if (!changed) return null;

            // Mark transaction as not affecting history
            tr.setMeta("add-block-ids", true);
            tr.setMeta("addToHistory", false);

            return tr;
        },
    });
}
